import {Component} from 'react'
import {Field, formValueSelector, reduxForm} from 'redux-form'
import {parseISO, parse, isValid, differenceInDays, differenceInMonths, differenceInYears} from 'date-fns'
import Tooltip from 'rc-tooltip'
import PropTypes from 'prop-types'
import HelpCircle from 'react-feather/dist/icons/help-circle.js'
import {connect} from 'react-redux'

import DateField from '../../Form/Field/Date.jsx'
import FormGlobalErrors from '../../Form/FormGlobalErrors.jsx'
import Input from '../../Form/Field/Input.jsx'
import Loader from '../../../../shared/components/Loader/Loader.jsx'
import RemoveCdiModal from '../../Modals/RemoveCdiModal/RemoveCdiModal.jsx'
import Select from '../../Form/Field/Select.jsx'

import connectModal from '../../../../shared/components/Modals/connect.js'
import createOnSubmitFailHandler from '../../Form/createOnSubmitFailHandler.js'
import normBoolean from '../../Form/Normalizer/boolean.js'
import validate from './validate.js'

import api from '../../../../api.js'

import './AdminFreelanceMonitorForm.scss'

const tryParseDate = (dateStr) => {
  let d = parseISO(dateStr)
  if (isValid(d)) return d
  d = parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date())
  if (isValid(d)) return d
  return null
}

const relativeDateCaption = (dateStr) => {
  if (!dateStr) return null
  try {
    const date = tryParseDate(dateStr)
    if (!date) return null
    const today = new Date()
    const diffDays = differenceInDays(date, today)

    let label
    let modifier

    if (diffDays === 0) {
      label = "Aujourd'hui"
      modifier = 'past'
    } else {
      const absDays = Math.abs(diffDays)
      const isPast = diffDays < 0
      modifier = isPast ? 'past' : 'future'

      if (absDays < 60) {
        label = `${absDays} jour${absDays > 1 ? 's' : ''}`
      } else {
        const absMonths = Math.abs(differenceInMonths(date, today))
        if (absMonths < 24) {
          label = `${absMonths} mois`
        } else {
          const absYears = Math.abs(differenceInYears(date, today))
          label = `${absYears} an${absYears > 1 ? 's' : ''}`
        }
      }

      label = isPast ? `Il y a ${label}` : `Dans ${label}`
    }

    return <span className={`form-caption form-caption--${modifier}`}>{label}</span>
  } catch {
    return null
  }
}

export const name = 'AdminFreelanceMonitorForm'

class AdminFreelanceMonitorForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      agents: [],
    }
  }

  static propTypes = {
    className: PropTypes.string,
    isReviewed: PropTypes.bool,
    hasSkillsFileUploaded: PropTypes.bool
  }

  static defaultProps = {
    className: '',
    isReviewed: false,
    hasSkillsFileUploaded: false
  }

  componentDidMount = async () => {
    if (null !== this.props.currentUniverseId) {
      const agents = (await api.users.getBusinessManagersByUniverse({
        universe: this.props.currentUniverseId
      })).data
      this.setState({agents})
    }
  }

  handleCdiChange = (event) => {
    const newValue = event.target.checked
    this.props.change('freelance.cdi', newValue)
    if (true === newValue) {
      this.triggerPublicChange(false)
    }
  }

  handlePublicChange = (event, newValue) => {
    if (true === newValue && this.props.cdiChecked) {
      this.props.openRemoveCdiModal({
        triggerCdiChange: this.triggerCdiChange,
        triggerPublicChange: this.triggerPublicChange
      })
    }
  }

  handleSeenChange = (event, newValue) => {
    if (newValue === true) {
      this.props.change('freelance.no_show', false)
    }
  }

  handleNoShowChange = (event, newValue) => {
    if (newValue === true) {
      this.props.change('freelance.seen', false)
    }
  }

  /**
   * @param {boolean} newValue
   */
  triggerPublicChange = (newValue= false) => {
    this.props.change('freelance.public', newValue)
  }

  /**
   * @param {boolean} newValue
   */
  triggerCdiChange = (newValue= false) => {
    this.props.change('freelance.cdi', newValue)
  }

  render() {
    const reviewedId = 'reviewed'
    const hasStoppedSms = 'has-stopped-sms'
    const skillsFileUploadedId = 'skills-file-uploaded'
    const checkedDcUploaded = (this.props.currentRegistrationStep && this.props.currentRegistrationStep === '7') ||
      this.props.hasSkillsFileUploaded

    const lastType1Entry = (this.props.history ?? []).reduceRight((found, entry) => found ?? (entry.description?.includes('Date de disponibilité') ? entry : null), null)

    return (
      <div id={name} className={this.props.className}>
        <form autoComplete="off" onSubmit={this.props.handleSubmit}>
          <div className="row mb-5">
            <div className="col-xl-6">
              <h5>Validation profil</h5>

              <div
                className="d-flex flex-wrap flex-grow-1 my-3 monitor-checkbox-container" style={{gap: 8}}>
                <Field
                  name="user.complete"
                  type="checkbox"
                  normalize={normBoolean}
                  component={Input}
                  className="monitor-checkbox"
                  label="Complet"
                />

                <Field
                  name="freelance.seen"
                  type="checkbox"
                  normalize={normBoolean}
                  component={Input}
                  className="monitor-checkbox"
                  label="Vu en rendez-vous"
                  onChange={this.handleSeenChange}
                />

                <Field
                  name="freelance.public"
                  type="checkbox"
                  normalize={normBoolean}
                  onChange={this.handlePublicChange}
                  component={Input}
                  className="monitor-checkbox"
                  label="Public"
                />

                <div className="form-group">
                  <input
                    id={reviewedId}
                    checked={this.props.isReviewed}
                    type="checkbox"
                    className="monitor-checkbox"
                    disabled={true}
                    aria-disabled={true}
                  />

                  <label htmlFor={reviewedId}>Noté</label>
                </div>

                <div className="form-group">
                  <input
                    id={skillsFileUploadedId}
                    checked={checkedDcUploaded}
                    type="checkbox"
                    className="monitor-checkbox"
                    disabled={true}
                    aria-disabled={true}
                  />

                  <label htmlFor={skillsFileUploadedId}>Dc Uploadé</label>
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <h5>Alertes & exclusions</h5>

              <div className="d-flex flex-wrap flex-grow-1 my-3 monitor-checkbox-container" style={{gap: 8}}>
                <Field
                  name="user.gold"
                  type="checkbox"
                  normalize={normBoolean}
                  component={Input}
                  className="monitor-checkbox"
                  label={<>
                    Label Gold
                    <Tooltip
                      placement="top"
                      trigger={['hover', 'click', 'focus']}
                      overlay={
                        <span>
                          Lorsqu'un profil est "gold", une médaille apparait sur sa carte (uniquement pour les
                          utilisateurs ayant accès à ce volet), il remontera plus haut dans les recherche et il peut
                          être filtré dans la recherche
                        </span>
                      }
                    >
                      <HelpCircle size={20}/>
                    </Tooltip>
                  </>}
                />

                <Field
                  name="user.unreliable"
                  type="checkbox"
                  normalize={normBoolean}
                  component={Input}
                  className="monitor-checkbox monitor-checkbox--warning"
                  label="Profil douteux"
                />

                <Field
                  name="freelance.no_show"
                  type="checkbox"
                  normalize={normBoolean}
                  component={Input}
                  className="monitor-checkbox monitor-checkbox--warning"
                  label="No show"
                  onChange={this.handleNoShowChange}
                />

                <div className="form-group">
                  <input
                    id={hasStoppedSms}
                    checked={this.props.hasStoppedSms}
                    type="checkbox"
                    className="monitor-checkbox monitor-checkbox--warning"
                    disabled={true}
                    aria-disabled={true}
                  />

                  <label htmlFor={hasStoppedSms}>Stop SMS</label>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mb-3">
            <h5 className="mb-0">
              Échéances clés
            </h5>

            <div className="custom-control custom-switch">
              <input
                className="custom-control-input"
                name="freelance.cdi"
                type="checkbox"
                id="freelanceCDI"
                checked={!!this.props.cdiChecked}
                onChange={this.handleCdiChange}
              />

              <label className="custom-control-label d-flex align-items-center font-weight-bold" htmlFor={`freelanceCDI`}>
                Profil en CDI
                <Tooltip
                  placement="top"
                  trigger={['hover', 'click', 'focus']}
                  overlay={
                    <span>Un freelance indiqué en cdi n'est plus visible par les visiteurs du site.</span>
                  }
                >
                  <HelpCircle size={20} className="ml-1"/>
                </Tooltip>
              </label>
            </div>
          </div>

          <div className="d-flex flex-wrap flex-md-nowrap mb-5" style={{gap: 12}}>
            <div className="col-12 col-xl-4 bg-gray-100 flex-fill px-4 py-3 radius-lg">
              <Field
                label="Disponibilité"
                name="freelance.date_available"
                placeholder="JJ/MM/AAAA"
                type="text"
                component={DateField}/>
              {relativeDateCaption(this.props.dateAvailable)}
              {lastType1Entry?.moderator && <span className="form-caption">Date mise à jour par {lastType1Entry.moderator.fullname}</span>}
            </div>

            <div className="col-12 col-xl-4 bg-gray-100 flex-fill px-4 py-3 radius-lg">
              <Field
                label="Prochaine relance"
                name="freelance.date_next_action"
                placeholder="JJ/MM/AAAA"
                type="text"
                component={DateField}/>
              {relativeDateCaption(this.props.dateNextAction)}
            </div>

            <div className={`col-12 col-xl-4 bg-gray-100 flex-fill px-4 py-3 radius-lg ${!this.props.cdiChecked ? 'invisible' : ''}`}>
              <Field
                label="Début de CDI"
                name="freelance.date_cdi_start"
                placeholder="JJ/MM/AAAA"
                type="text"
                disabled={!this.props.cdiChecked}
                component={DateField}/>
              {relativeDateCaption(this.props.dateCdiStart)}
            </div>
          </div>

          <h5 className="mb-3">
            Affectation
          </h5>

          <div className="d-flex flex-wrap mb-5" style={{gap: 12}}>
            <div className="col-12 col-xl-4 bg-gray-100 flex-fill px-4 py-3 radius-lg">
              <Field
                label="Agent du consultant"
                name="freelance.agent"
                multiple={false}
                component={Select}>
                <option/>
                {this.state.agents.map((agent) => (
                  <option value={agent.id} key={agent.id}>{agent.fullname}</option>
                ))}
              </Field>
            </div>
          </div>

          <input className="d-none" type="submit"/>

          <FormGlobalErrors className="mt-0" errors={this.props.error}/>
        </form>

        <Loader visible={this.props.submitting || this.props.triggerSubmit}/>
      </div>
    )
  }
}

const ReduxAdminFreelanceMonitorForm = reduxForm({
  form: name,
  validate,
  onSubmitFail: createOnSubmitFailHandler(name)
})(AdminFreelanceMonitorForm)

const selector = formValueSelector(name)

export default connectModal({RemoveCdiModal}, connect(
  state => ({
    cdiChecked: selector(state, 'freelance.cdi'),
    currentUniverseId: state?.universe?.currentUniverse?.id ?? null,
    dateAvailable: selector(state, 'freelance.date_available'),
    dateNextAction: selector(state, 'freelance.date_next_action'),
    dateCdiStart: selector(state, 'freelance.date_cdi_start'),
  })
)(ReduxAdminFreelanceMonitorForm))
